// BrimLight/client/ui/BrimLightAssistant.js
import { ComponentBase } from "../core/ComponentBase.js";

export class BrimLightAssistant extends ComponentBase {
    static styles = `
        /* Modal Styles - Mobile-first */
        .brimlight-assistant-modal .modal-dialog {
            margin: 0;
            max-width: 100%;
            height: 100%;
        }
        .brimlight-assistant-modal .modal-content {
            height: 100%;
            border-radius: 0;
            border: none;
        }
        .brimlight-assistant-modal .modal-body {
            flex-grow: 1;
            overflow-y: hidden; /* AiCoPilot handles its own scrolling */
            padding: 0;
            max-height: none;
        }
        
        /* Desktop styles for the modal */
        @media (min-width: 768px) {
            .brimlight-assistant-modal .modal-dialog {
                max-width: 500px;
                margin: 1.75rem auto;
                height: auto;
            }
            .brimlight-assistant-modal .modal-content {
                height: 80vh;
                border-radius: var(--bs-modal-border-radius);
                border: var(--bs-modal-border-width) solid var(--bs-modal-border-color);
            }
        }
    `;

    constructor(config) {
        super(config);
        this.toggleButton = null;
        this.modalElement = null;
        this.modalInstance = null;
        this.coPilotComponent = null;
        this.isModalVisible = false;

        this.agentName = this.config.agentName || "AssistantAgent";
        this.coPilotType = this.config.coPilotType || "AdvancedAIWorkspace";
        this.buttonIcon = this.config.buttonIcon || "bi-stars";
        this.buttonTooltip = this.config.buttonTooltip || "BrimLight Assistant";

        // --- NEW: Bind the project selection handler ---
        this._boundHandleProjectSelected = this.handleProjectSelected.bind(this);

        this.applyStyles();
    }

    static getDefaultConfig() {
        return {
            agentName: "AssistantAgent",
            coPilotType: "AdvancedAIWorkspace",
            title: "BrimLight Assistant",
            buttonIcon: "bi-stars",
            buttonTooltip: "BrimLight Assistant",
            buttonClasses: "btn btn-outline-secondary header-btn",
        };
    }

    async initialize() {
        // --- NEW: Listen for the projectSelected event ---
        this.appClient.events.local.on('projectSelected', this._boundHandleProjectSelected);
        await super.initialize();
    }

    // --- NEW: Handle initial project context after login ---
    async initAfterLogin() {
        const selectedProject = this.appClient.getSharedData('selectedProject');
        if (selectedProject) {
            this.handleProjectSelected({ projectData: selectedProject });
        }
    }

    // --- NEW: Method to update the AiCoPilot's context ---
    handleProjectSelected({ projectData }) {
        const projectId = projectData ? projectData._id : null;
        this.log.info(`BrimLightAssistant detected project selection: ${projectId}`);
        if (this.coPilotComponent) {
            this.coPilotComponent.setContext({ projectId });
        }
    }

    createModalStructure() {
        if (this.modalElement) return;

        this.modalElement = document.createElement('div');
        this.modalElement.className = 'modal fade brimlight-assistant-modal';
        this.modalElement.id = `${this.config.instanceId}-modal`;
        this.modalElement.setAttribute('tabindex', '-1');
        this.modalElement.setAttribute('aria-hidden', 'true');

        this.modalElement.innerHTML = `
            <div class="modal-dialog modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">${this.t(this.config.title)}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body" id="${this.config.instanceId}-copilot-body"></div>
                </div>
            </div>
        `;
        document.body.appendChild(this.modalElement);
        this.modalInstance = new bootstrap.Modal(this.modalElement);

        this.modalElement.addEventListener('hidden.bs.modal', () => {
            this.isModalVisible = false;
        });

        this.modalElement.addEventListener('shown.bs.modal', () => {
            this.isModalVisible = true;
            if (this.coPilotComponent?.inputArea) {
                this.coPilotComponent.inputArea.focus();
            }
        });
    }

    async ensureCoPilotComponent() {
        if (this.coPilotComponent) return;
        if (!this.modalElement) this.createModalStructure();

        const coPilotBodyContainer = this.modalElement.querySelector(`#${this.config.instanceId}-copilot-body`);
        if (!coPilotBodyContainer) {
            this.log.error("CoPilot body container not found in modal.");
            return;
        }

        // @bundle-include: AdvancedAIWorkspace

        this.coPilotComponent = await this.createManagedComponent(this.coPilotType, coPilotBodyContainer, {
            instanceId: `${this.config.instanceId}-internal-copilot`,
            agentName: this.agentName,
            singleAgentMode: true,
            height: 'container'
        });

        // --- NEW: Set initial context when component is created ---
        const selectedProject = this.appClient.getSharedData('selectedProject');
        if (selectedProject) {
            this.coPilotComponent.setContext({ projectId: selectedProject._id });
        }
    }

    async show() {
        if (!this.modalInstance) this.createModalStructure();
        await this.ensureCoPilotComponent();
        this.modalInstance.show();
    }

    hide() {
        this.modalInstance?.hide();
    }

    toggle() {
        this.isModalVisible ? this.hide() : this.show();
    }

    render() {
        if (!this.toggleButton) {
            this.toggleButton = document.createElement('button');
            this.toggleButton.id = `${this.config.instanceId}-toggle`;
            this.toggleButton.className = this.config.buttonClasses;
            this.toggleButton.title = this.t(this.config.buttonTooltip);
            this.toggleButton.innerHTML = `<i class="bi ${this.config.buttonIcon}"></i>`;
            this.toggleButton.addEventListener('click', this.toggle.bind(this));
        }
        return this.toggleButton;
    }

    destroy() {
        this.appClient.events.local.off('projectSelected', this._boundHandleProjectSelected);

        this.toggleButton?.removeEventListener('click', this.toggle.bind(this));
        this.coPilotComponent?.destroy();
        this.modalInstance?.dispose();
        this.modalElement?.remove();
        super.destroy();
    }
}